import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProposalForm } from '../../proposals/components/ProposalForm';
import { isSpreadProposal } from '../../proposals/prototype/spreadProposalStore';
import type { ProposalFormData } from '../../proposals/types';
import { ProposalRecord, proposalService } from '../../proposals/services/proposalService';
import { useAuth } from '../../../lib/auth/AuthContext';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';

interface MarketingDocument {
  id: string;
  title: string;
  type: 'presentation' | 'flyer' | 'datasheet';
  downloadUrl: string;
}

const MARKETING_DOCUMENTS: MarketingDocument[] = [
  { id: '1', title: 'Product Overview Presentation', type: 'presentation', downloadUrl: '#' },
  { id: '2', title: 'Solution Capabilities Flyer', type: 'flyer', downloadUrl: '#' },
  { id: '3', title: 'Technical Specifications', type: 'datasheet', downloadUrl: '#' },
  { id: '4', title: 'Customer Success Stories', type: 'presentation', downloadUrl: '#' },
  { id: '5', title: 'Product Features Datasheet', type: 'datasheet', downloadUrl: '#' },
];

const formatCurrency = (v: number) =>
  `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const PDFLink: React.FC<{ url: string | null; label: string }> = ({ url, label }) => {
  if (!url) return <span className="text-heritage-grey">—</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm text-blueprint-blue hover:text-hero-red"
      title={label}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
      {label}
    </a>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isProposalFormOpen, setIsProposalFormOpen] = useState(false);
  const [isMarketingModalOpen, setIsMarketingModalOpen] = useState(false);
  const [editingProposal, setEditingProposal] = useState<ProposalRecord | null>(null);
  const [proposals, setProposals] = useState<ProposalRecord[]>([]);
  const [loadingProposals, setLoadingProposals] = useState(true);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [versionHistory, setVersionHistory] = useState<Record<string, ProposalRecord[]>>({});
  const [loadingVersions, setLoadingVersions] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; groupId: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Deduplicate by groupId — keep only the highest-version proposal per group.
  // Guards against data inconsistency where multiple is_latest=true records exist
  // for the same group (which would cause the same project to appear twice as "v1").
  const deduplicatedProposals = useMemo(() => {
    const groupMap = new Map<string, ProposalRecord>();
    for (const p of proposals) {
      const existing = groupMap.get(p.groupId);
      if (
        !existing ||
        p.version > existing.version ||
        (p.version === existing.version &&
          p.createdAt &&
          existing.createdAt &&
          new Date(p.createdAt) > new Date(existing.createdAt))
      ) {
        groupMap.set(p.groupId, p);
      }
    }
    return Array.from(groupMap.values()).sort((a, b) => {
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [proposals]);

  const filteredProposals = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return deduplicatedProposals;
    return deduplicatedProposals.filter(p =>
      p.refNumber?.toLowerCase().includes(q) ||
      p.customerName?.toLowerCase().includes(q) ||
      p.projectTitle?.toLowerCase().includes(q)
    );
  }, [deduplicatedProposals, searchQuery]);

  const loadProposals = () => {
    setLoadingProposals(true);
    proposalService.getProposals()
      .then(setProposals)
      .catch(err => console.error('Failed to load proposals:', err))
      .finally(() => setLoadingProposals(false));
  };

  useEffect(() => { loadProposals(); }, []);

  const handleFormClose = () => {
    setIsProposalFormOpen(false);
    setEditingProposal(null);
    loadProposals();
  };

  const handleProposalSubmit = (_data: ProposalFormData) => {};

  // Spread proposals reopen in the spread flow; anything saved by the older
  // form keeps opening in that form, since its stored shape is different.
  const handleEdit = (proposal: ProposalRecord) => {
    if (isSpreadProposal(proposal.formData)) {
      navigate(`/proposals/${proposal.id}`);
      return;
    }
    setEditingProposal(proposal);
  };

  const handleDelete = (id: string, groupId: string) => {
    setConfirmDelete({ id, groupId });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const { id, groupId } = confirmDelete;
    setConfirmDelete(null);
    setDeletingId(id);
    try {
      await proposalService.deleteProposal(id);
      loadProposals();
      if (expandedGroupId === groupId) {
        const versions = await proposalService.getProposalVersions(groupId);
        setVersionHistory(prev => ({ ...prev, [groupId]: versions }));
      }
    } catch (err) {
      console.error('Failed to delete proposal:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const canDelete = (proposal: ProposalRecord) =>
    user?.role === 'Admin' || (!!user?.email && proposal.createdBy === user.email);

  const toggleHistory = async (groupId: string) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(groupId);
    if (!versionHistory[groupId]) {
      setLoadingVersions(groupId);
      try {
        const versions = await proposalService.getProposalVersions(groupId);
        setVersionHistory(prev => ({ ...prev, [groupId]: versions }));
      } catch (err) {
        console.error('Failed to load versions:', err);
      } finally {
        setLoadingVersions(null);
      }
    }
  };

  const getDocumentIcon = (type: MarketingDocument['type']) => {
    switch (type) {
      case 'presentation':
        return (
          <svg className="w-6 h-6 text-blueprint-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
        );
      case 'flyer':
        return (
          <svg className="w-6 h-6 text-galvanizing-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'datasheet':
        return (
          <svg className="w-6 h-6 text-readable-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  return (
    <div className="p-6 w-full min-h-screen bg-page-bg">
      <h1 className="text-2xl font-bold text-blueprint-blue font-heading mb-8">Proposals</h1>

      {/* Proposals Table */}
      <div className="bg-white rounded-xl shadow-card mb-8">
        <div className="p-6 border-b border-[#E2E5EA] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-blueprint-blue font-heading">Proposals</h2>
          <button
            onClick={() => navigate('/proposals/new')}
            className="px-4 py-2 bg-readable-red text-white rounded-lg hover:bg-hero-red-70 text-sm font-medium"
          >
            + New Proposal
          </button>
        </div>

        {/* Filter bar */}
        <div className="px-6 py-3 border-b border-[#EEF0F3] flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-heritage-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by ref, customer or project..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-[#E2E5EA] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="text-xs text-heritage-grey hover:text-blueprint-blue"
            >
              Clear
            </button>
          )}
          {searchQuery && (
            <span className="text-xs text-heritage-grey">
              {filteredProposals.length} of {proposals.length} results
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          {loadingProposals ? (
            <div className="p-8 text-center text-heritage-grey">Loading proposals...</div>
          ) : proposals.length === 0 ? (
            <div className="p-8 text-center text-heritage-grey">No proposals yet. Create your first one.</div>
          ) : filteredProposals.length === 0 ? (
            <div className="p-8 text-center text-heritage-grey">No proposals match your search.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-page-bg border-b border-[#E2E5EA]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-text-grey">Ref #</th>
                  <th className="text-left px-4 py-3 font-medium text-text-grey">Ver</th>
                  <th className="text-left px-4 py-3 font-medium text-text-grey">Customer</th>
                  <th className="text-left px-4 py-3 font-medium text-text-grey">Project</th>
                  <th className="text-right px-4 py-3 font-medium text-text-grey">Total</th>
                  <th className="text-left px-4 py-3 font-medium text-text-grey">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-text-grey">Customer PDF</th>
                  <th className="text-left px-4 py-3 font-medium text-text-grey">Internal PDF</th>
                  <th className="text-left px-4 py-3 font-medium text-text-grey">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProposals.map(p => (
                  <React.Fragment key={p.id}>
                    <tr className="border-b border-[#EEF0F3] hover:bg-page-bg">
                      <td className="px-4 py-3 font-mono text-text-grey">{p.refNumber}</td>
                      <td className="px-4 py-3 text-text-grey">v{p.version}</td>
                      <td className="px-4 py-3 text-text-grey">{p.customerName || '—'}</td>
                      <td className="px-4 py-3 text-text-grey">{p.projectTitle || '—'}</td>
                      <td className="px-4 py-3 text-right font-medium text-blueprint-blue">{formatCurrency(p.totalPrice)}</td>
                      <td className="px-4 py-3 text-heritage-grey">{formatDate(p.createdAt)}</td>
                      <td className="px-4 py-3"><PDFLink url={p.customerPdfUrl} label="Download" /></td>
                      <td className="px-4 py-3"><PDFLink url={p.internalPdfUrl} label="Download" /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleEdit(p)}
                            className="text-blueprint-blue hover:text-hero-red font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => toggleHistory(p.groupId)}
                            className="text-heritage-grey hover:text-blueprint-blue font-medium"
                          >
                            {expandedGroupId === p.groupId ? 'Hide' : 'History'}
                          </button>
                          {canDelete(p) && (
                            <button
                              onClick={() => handleDelete(p.id, p.groupId)}
                              disabled={deletingId === p.id}
                              className="text-readable-red hover:text-readable-red font-medium disabled:opacity-40"
                            >
                              {deletingId === p.id ? '…' : 'Delete'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Version history sub-rows */}
                    {expandedGroupId === p.groupId && (
                      loadingVersions === p.groupId ? (
                        <tr key={`${p.groupId}-loading`}>
                          <td colSpan={9} className="px-8 py-3 text-sm text-heritage-grey bg-page-bg">
                            Loading history...
                          </td>
                        </tr>
                      ) : (
                        (versionHistory[p.groupId] ?? []).filter(v => v.id !== p.id).map(v => (
                          <tr
                            key={v.id}
                            className={`border-b border-[#EEF0F3] text-heritage-grey ${v.deletedAt ? 'bg-hero-red-10' : 'bg-page-bg'}`}
                          >
                            <td className={`px-8 py-2 font-mono text-xs ${v.deletedAt ? 'line-through text-heritage-grey' : ''}`}>
                              {v.refNumber}
                            </td>
                            <td className="px-4 py-2 text-xs">
                              v{v.version}
                              {v.isLatest && !v.deletedAt && (
                                <span className="ml-1 text-galvanizing-green font-medium">(latest)</span>
                              )}
                              {v.deletedAt && (
                                <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-hero-red-10 text-readable-red font-medium">
                                  deleted
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-xs">{v.customerName || '—'}</td>
                            <td className="px-4 py-2 text-xs">{v.projectTitle || '—'}</td>
                            <td className="px-4 py-2 text-xs text-right">{formatCurrency(v.totalPrice)}</td>
                            <td className="px-4 py-2 text-xs">{formatDate(v.createdAt)}</td>
                            <td className="px-4 py-2 text-xs">
                              {v.deletedAt ? <span className="text-heritage-grey">—</span> : <PDFLink url={v.customerPdfUrl} label="Download" />}
                            </td>
                            <td className="px-4 py-2 text-xs">
                              {v.deletedAt ? <span className="text-heritage-grey">—</span> : <PDFLink url={v.internalPdfUrl} label="Download" />}
                            </td>
                            <td className="px-4 py-2 text-xs">
                              {!v.deletedAt && canDelete(v) && (
                                <button
                                  onClick={() => handleDelete(v.id, v.groupId)}
                                  disabled={deletingId === v.id}
                                  className="text-readable-red hover:text-readable-red disabled:opacity-40"
                                >
                                  {deletingId === v.id ? '…' : 'Delete'}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Marketing Materials Modal */}
      {isMarketingModalOpen && (
        <div className="fixed inset-0 bg-blueprint-blue/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-[#E2E5EA] flex justify-between items-center">
              <h2 className="text-xl font-semibold text-blueprint-blue font-heading">Marketing Materials</h2>
              <button onClick={() => setIsMarketingModalOpen(false)} className="text-heritage-grey hover:text-blueprint-blue">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-8rem)]">
              <div className="space-y-4">
                {MARKETING_DOCUMENTS.map(doc => (
                  <div
                    key={doc.id}
                    className="flex items-center space-x-4 p-4 bg-page-bg rounded-lg hover:bg-page-bg transition-colors cursor-pointer"
                  >
                    {getDocumentIcon(doc.type)}
                    <div className="flex-grow">
                      <h3 className="font-medium text-blueprint-blue">{doc.title}</h3>
                      <p className="text-sm text-heritage-grey capitalize">{doc.type}</p>
                    </div>
                    <a
                      href={doc.downloadUrl}
                      className="px-3 py-1.5 text-sm text-text-grey border border-[#E2E5EA] rounded-lg hover:bg-page-bg"
                      onClick={e => { if (doc.downloadUrl === '#') e.preventDefault(); }}
                    >
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <ProposalForm
        key={editingProposal?.id ?? 'new'}
        isOpen={isProposalFormOpen || editingProposal !== null}
        onClose={handleFormClose}
        onSubmit={handleProposalSubmit}
        initialData={editingProposal ?? undefined}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Proposal"
        message="Are you sure you want to delete this proposal version?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default Dashboard;
